// src/common/pages/authorValidation/FormularioDeCampos.tsx
import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { isValidPhoneNumber } from "react-phone-number-input";
import { normalizeAndValidateEmail } from "../../utils";
import { validateMask } from "../../../entities/common/utils";
import { CampoEditable, valoresElegibles } from "./CampoEditable";
import { cuitConMascara, estaVacio, sinCambiar } from "./datos";
import type { TextosPagina } from "./textos";
import type { CampoConfig, Idioma, OpcionDeCatalogo, Valores } from "./tipos";

/**
 * Las máscaras con que se valida un campo. Salvo el número de documento, las que
 * trae el registro. Ese trae las de todos los tipos juntas, y cuál rige lo decide
 * el tipo de documento elegido, como en el store generado de AuthorComposer
 * (AuthorComposerCreateStore.ts): un DU (DNI) lleva solo dígitos, un CUIT/CUIL
 * lleva guiones, y con cualquier otro tipo (un pasaporte) o sin tipo no hay máscara.
 */
export function mascarasDelCampo(campo: CampoConfig, valores: Valores): string[] {
  const mascaras = campo.masks ?? [];
  if (campo.name !== "taxId") return mascaras;
  switch (valores.taxIdType) {
    case "du":
      return mascaras.filter((m) => !m.includes("-"));
    case "cuitCuil":
      return mascaras.filter((m) => m.includes("-"));
    default:
      return [];
  }
}

/**
 * Los valores con que arranca un formulario donde el autor corrige lo guardado:
 * un valor con máscara que no la cumple pero cuyos dígitos sí (un DU guardado
 * como "12.345.678", dato viejo) se muestra en dígitos, para que no aparezca como
 * «Formato inválido» un dato que solo tiene puntos. Lo demás queda como está.
 */
export function valoresParaCorregir(campos: CampoConfig[], valores: Valores): Valores {
  const salida = { ...valores };
  for (const campo of campos) {
    const valor = valores[campo.name] ?? "";
    const mascaras = mascarasDelCampo(campo, valores);
    if (estaVacio(valor) || mascaras.length === 0) continue;
    const cumple = (texto: string) => mascaras.some((m) => validateMask(texto, { mask: m }));
    if (cumple(valor.trim())) continue;
    const digitos = valor.replace(/\D/g, "");
    if (digitos && cumple(digitos)) salida[campo.name] = digitos;
  }
  return salida;
}

/**
 * Un CUIT/CUIL se lleva a su máscara antes de validar, con la regla de
 * `cuitConMascara` (datos.ts): escrito sin guiones (en el teléfono es lo
 * natural: «20281234561»), con otros separadores o con diez dígitos. Va así al
 * server y así queda en el campo. Si la regla no da una máscara (una letra, un
 * dígito de menos), queda como se escribió y validarCampos dice el formato.
 * Lo demás queda como está.
 */
export function llevarALaMascara(campos: CampoConfig[], valores: Valores): Valores {
  const valor = valores.taxId ?? "";
  if (!campos.some((c) => c.name === "taxId") || valores.taxIdType !== "cuitCuil" || estaVacio(valor)) return valores;
  const conMascara = cuitConMascara(valor);
  return conMascara !== null && conMascara !== valor ? { ...valores, taxId: conMascara } : valores;
}

/**
 * Valida con las mismas funciones que ExternalValidationForm (requerido, email,
 * máscara y teléfono), con las máscaras del documento según su tipo. Una opción
 * guardada que ya no está entre las que ofrece la lista (una provincia que no es
 * del país elegido) cuenta como vacía. Un CUIT/CUIL vale solo si ya está como lo
 * deja `cuitConMascara`, que es como se envía (el formulario ya intentó llevarlo
 * a la máscara); si no, el error dice el formato.
 *
 * Lo que el autor no cambió frente a `guardado` (lo guardado crudo, como lo da
 * `entityData`; ver `sinCambiar`) no da error de formato: un número o una fecha
 * viejos que la app y el server aceptaron no frenan la confirmación. Vacío,
 * sigue siendo requerido.
 */
export function validarCampos(
  campos: CampoConfig[],
  obligatorios: readonly string[],
  valores: Valores,
  textos: TextosPagina,
  catalogos: Record<string, OpcionDeCatalogo[]>,
  guardado: Record<string, unknown> = {}
): Record<string, string> {
  const errores: Record<string, string> = {};
  for (const campo of campos) {
    const valor = valores[campo.name] ?? "";
    const elegibles = valoresElegibles(campo, valores, catalogos);
    if (estaVacio(valor) || (elegibles !== null && !elegibles.includes(valor))) {
      if (obligatorios.includes(campo.name)) errores[campo.name] = textos.requerido;
      continue;
    }
    if (sinCambiar(campo.name, valor, valores, campos, guardado)) continue;
    const texto = valor.trim();
    const mascaras = mascarasDelCampo(campo, valores);
    const esCuit = campo.name === "taxId" && valores.taxIdType === "cuitCuil";
    if (campo.email && normalizeAndValidateEmail(texto) === null) {
      errores[campo.name] = textos.emailInvalido;
    } else if (esCuit && cuitConMascara(texto) !== texto) {
      errores[campo.name] = textos.formatoCuit;
    } else if (mascaras.length > 0 && !mascaras.some((m) => validateMask(texto, { mask: m }))) {
      errores[campo.name] = esCuit ? textos.formatoCuit : textos.formatoInvalido;
    } else if (campo.phone && !isValidPhoneNumber(texto)) {
      errores[campo.name] = textos.telefonoInvalido;
    }
  }
  return errores;
}

/**
 * Los campos cuyo error depende de lo elegido en otro: no cambia su valor, pero
 * sí si es válido (el formato del número lo decide el tipo de documento, ver
 * mascarasDelCampo). Al cambiar el otro, el error que traían deja de valer hasta
 * el próximo envío.
 */
const SE_VALIDA_SEGUN: Record<string, readonly string[]> = { taxId: ["taxIdType"] };

/**
 * Lo que se vacía cuando cambia otro dato: al cambiar alguno de `si` que tenía un valor frente a como
 * empezó el formulario (un dato que estaba vacío y se completa no es otro domicilio), los de `vaciar` que tenían algo y el autor no escribió quedan vacíos, con lo de antes
 * debajo (`textoAntes`, con `{valor}`). El domicilio del resumen (KAN-456): otra calle, provincia o
 * localidad, y el código postal, el piso y el departamento son del domicilio viejo.
 */
export interface VaciarAlCambiar {
  si: readonly string[];
  vaciar: readonly string[];
  textoAntes: string;
}

/**
 * Aplica `regla` a los valores que siguen a un cambio. Devuelve los valores y lo de antes de cada
 * campo vaciado. Si lo de `si` vuelve a ser lo del principio, lo vaciado vuelve (lo que el autor
 * escribió después queda). Un dato de `si` que empezó vacío no dispara nada. `escritos` son los campos que el autor escribió: esos no se vacían.
 */
export function vaciarSiCambia(
  regla: VaciarAlCambiar,
  iniciales: Valores,
  siguientes: Valores,
  antes: Record<string, string>,
  escritos: ReadonlySet<string>
): { valores: Valores; antes: Record<string, string> } {
  const igual = (n: string) => String(siguientes[n] ?? "").trim() === String(iniciales[n] ?? "").trim();
  const valores = { ...siguientes };
  const cambioConValor = (n: string) => !igual(n) && !estaVacio(iniciales[n]);
  if (!regla.si.some(cambioConValor)) {
    for (const [nombre, previo] of Object.entries(antes)) if (estaVacio(valores[nombre])) valores[nombre] = previo;
    return { valores, antes: {} };
  }
  const nuevosAntes = { ...antes };
  for (const nombre of regla.vaciar) {
    if (nombre in nuevosAntes || escritos.has(nombre) || estaVacio(valores[nombre])) continue;
    nuevosAntes[nombre] = valores[nombre];
    valores[nombre] = "";
  }
  return { valores, antes: nuevosAntes };
}

export interface FormularioDeCamposProps {
  campos: CampoConfig[];
  obligatorios: readonly string[];
  valoresIniciales: Valores;
  catalogos: Record<string, OpcionDeCatalogo[]>;
  idioma: Idioma;
  textos: TextosPagina;
  textoBoton: string;
  /** Lo que se espera mientras `ocupado`: nombra al botón, que se queda sin texto. */
  textoEspera: string;
  ocupado: boolean;
  erroresDelServidor?: Record<string, string>;
  /**
   * Lo guardado del autor tal como llega (`entityData`): lo que el autor no
   * cambió frente a esto no da error de formato (ver validarCampos).
   */
  guardado?: Record<string, unknown>;
  /** Ayudas debajo de algunos campos, por nombre. */
  ayudas?: Record<string, string>;
  /** Lo que se vacía cuando cambia otro dato (ver VaciarAlCambiar). */
  vaciarAlCambiar?: VaciarAlCambiar;
  onEnviar: (valores: Valores) => void;
}

export const FormularioDeCampos: React.FC<FormularioDeCamposProps> = ({
  campos,
  obligatorios,
  valoresIniciales,
  catalogos,
  idioma,
  textos,
  textoBoton,
  textoEspera,
  ocupado,
  erroresDelServidor,
  guardado,
  ayudas,
  vaciarAlCambiar,
  onEnviar,
}) => {
  const [valores, setValores] = useState<Valores>(valoresIniciales);
  // Lo que había en cada campo que vació `vaciarAlCambiar`, y los campos que el autor escribió.
  const [antes, setAntes] = useState<Record<string, string>>({});
  const [escritos, setEscritos] = useState<ReadonlySet<string>>(new Set());
  const [errores, setErrores] = useState<Record<string, string>>({});
  // Los campos que el autor tocó desde el último envío: el error que el server
  // dio sobre ellos ya no vale hasta que se vuelvan a enviar.
  const [editados, setEditados] = useState<Record<string, boolean>>({});
  const [enviosFallidos, setEnviosFallidos] = useState(0);
  const contenedor = useRef<HTMLFieldSetElement>(null);

  // Con errores, el foco va al primer campo marcado: en un teléfono el formulario
  // es más alto que la pantalla y el error puede quedar fuera de la vista.
  useEffect(() => {
    if (enviosFallidos === 0) return;
    contenedor.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [enviosFallidos]);

  const cambiar = (nombre: string, valor: string) => {
    // Una provincia es de un país: cambiar el país la deja sin elegir.
    const dependientes = campos.filter((c) => c.compositeWith === nombre).map((c) => c.name);
    // El número de documento sigue siendo el mismo, pero qué formato vale lo decide el tipo.
    const revalidables = campos.filter((c) => (SE_VALIDA_SEGUN[c.name] ?? []).includes(nombre)).map((c) => c.name);
    const siguientes = { ...valores, [nombre]: valor };
    for (const d of dependientes) siguientes[d] = "";
    const disparan = vaciarAlCambiar && [nombre, ...dependientes].some((n) => vaciarAlCambiar.si.includes(n));
    const resultado = disparan
      ? vaciarSiCambia(vaciarAlCambiar, valoresIniciales, siguientes, antes, escritos)
      : { valores: siguientes, antes };
    setValores(resultado.valores);
    setAntes(resultado.antes);
    if (!escritos.has(nombre)) setEscritos(new Set([...escritos, nombre]));
    setErrores((previos) => {
      const siguientes = { ...previos };
      for (const n of [nombre, ...revalidables]) delete siguientes[n];
      return siguientes;
    });
    // El error que el server dio antes no corresponde a lo que cambió ni a lo que quedó sin elegir.
    setEditados((previos) => ({
      ...previos,
      ...Object.fromEntries([nombre, ...dependientes, ...revalidables].map((n) => [n, true])),
    }));
  };

  const enviar = () => {
    const aValidar = llevarALaMascara(campos, valores);
    if (aValidar !== valores) setValores(aValidar);
    const encontrados = validarCampos(campos, obligatorios, aValidar, textos, catalogos, guardado);
    setErrores(encontrados);
    if (Object.keys(encontrados).length > 0) {
      setEnviosFallidos((n) => n + 1);
      return;
    }
    setEditados({});
    onEnviar(aValidar);
  };

  return (
    <Box>
      {/* Mientras se envía, los campos no se editan. */}
      <Box
        component="fieldset"
        ref={contenedor}
        disabled={ocupado}
        sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
      >
        {campos.map((campo) => (
          <CampoEditable
            key={campo.name}
            campo={campo}
            valores={valores}
            catalogos={catalogos}
            idioma={idioma}
            // Los no obligatorios vacíos se rotulan «(Opcional)».
            opcional={!obligatorios.includes(campo.name)}
            textoOpcional={textos.opcional}
            error={errores[campo.name] ?? (editados[campo.name] ? undefined : erroresDelServidor?.[campo.name])}
            ayuda={
              ayudas?.[campo.name] ??
              (vaciarAlCambiar && antes[campo.name] !== undefined
                ? vaciarAlCambiar.textoAntes.replace("{valor}", () => antes[campo.name])
                : undefined)
            }
            onChange={(v) => cambiar(campo.name, v)}
          />
        ))}
      </Box>
      {/* Ocupado, el botón se queda sin texto: lo nombra la espera. El aria-label del progreso no
          alcanza: dentro de un botón, un progressbar aporta su valor al nombre, no su etiqueta. */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={ocupado}
        aria-label={ocupado ? textoEspera : undefined}
        onClick={enviar}
      >
        {ocupado ? <CircularProgress size={24} color="inherit" aria-label={textoEspera} /> : textoBoton}
      </Button>
    </Box>
  );
};
